import cx from 'classnames';
import dynamic from 'next/dynamic';
import { type PropsWithChildren, useContext } from 'react';
import TrackWrapper from '@/common/TrackWrapper';
import AutoSelectContext from '@/new-cars/AutoSelect';
import {
  DRIVETRAIN_SECTION_ID,
  ENGINE_SECTION_ID,
  EXTERIOR_COLOR_SECTION_ID,
  INTERIOR_COLOR_SECTION_ID
} from '~/constants/new-cars/sectionIds';
import SectionName from '~/constants/new-cars/SectionName';
import { useIsMobile } from '~/context/common/DeviceContext';
import ParentModelContext from '~/context/new-cars/ParentModelContext';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import OptionSlideCard from './OptionSlideCard';
import { SectionHeading, SectionWrapper } from './OptionsSection.styles';

const viewEvent = {
  customAttributes: { action: 'viewed', level: 'Options', section: 'Options' },
  eventName: 'New Cars'
};

const OptionsCarousel = dynamic(() => import('./OptionsCarousel'), {
  ssr: false
});

type SubSectionProps = PropsWithChildren<{
  ariaLabel: string;
  id: string;
  isMobile: boolean;
  title: string;
  withDivider?: boolean;
}>;

function OptionsSubSection({
  ariaLabel,
  children,
  id,
  isMobile,
  title,
  withDivider = false
}: SubSectionProps) {
  if (isMobile) {
    return (
      <div className="scroll-mt-7xl" id={id}>
        <OptionsCarousel ariaLabel={ariaLabel} title={title}>
          {children}
        </OptionsCarousel>
      </div>
    );
  }

  return (
    <div
      className={cx('flex flex-col gap-xl px-2xl', {
        'border-t border-border-subtle pt-2xl': withDivider
      })}
    >
      <h2 className="scroll-mt-4xl t-heading-sm text-text-strong" id={id}>
        {title}
      </h2>
      <div className="grid grid-cols-3 gap-md lg:grid-cols-4">{children}</div>
    </div>
  );
}

type OwnProps = {
  heading?: string;
};

function OptionsSection({ heading }: OwnProps) {
  const { customizeOptions, selectedOptions } = useContext(ParentModelContext);
  const {
    setPendingOptionDeselection,
    setPendingOptionSelection,
    trimLevelSelections
  } = useContext(AutoSelectContext);
  const isMobile = useIsMobile();
  const { driveTypes, engines, exteriorColors, interiorColors } =
    customizeOptions.data;

  const isDriveTypeSingle = trimLevelSelections.driveType != null;
  const isEngineSingle = trimLevelSelections.engineCode != null;
  const isExteriorSingle = trimLevelSelections.exteriorColorCode != null;
  const isInteriorSingle = trimLevelSelections.interiorColorCode != null;

  const hasOptions =
    driveTypes.length > 0 ||
    engines.length > 0 ||
    exteriorColors.length > 0 ||
    interiorColors.length > 0;

  if (!hasOptions) return null;

  return (
    <TrackWrapper event={viewEvent} trackOnce>
      <SectionWrapper aria-labelledby="options-section-heading">
        <SectionHeading id="options-section-heading">
          {heading ?? 'Select Color and Options'}
        </SectionHeading>
        {exteriorColors.length > 0 && (
          <OptionsSubSection
            ariaLabel="Exterior colors"
            id={EXTERIOR_COLOR_SECTION_ID}
            isMobile={isMobile}
            title="Exterior Colors"
          >
            {exteriorColors.map(color => (
              <OptionSlideCard
                count={color.count}
                imageUrl={color.imageUrl}
                isGreyedOut={color.count === 0}
                isIncluded={isExteriorSingle}
                isSelected={color.code === selectedOptions?.exteriorColorCode}
                key={color.code}
                name={color.name}
                onSelect={() => {
                  if (color.code === selectedOptions?.exteriorColorCode) {
                    setPendingOptionDeselection('exterior', color.code);
                  } else {
                    setPendingOptionSelection('exterior', color.code);
                  }
                }}
                priceAdd={color.priceAdd}
                section="Exterior Colors"
              />
            ))}
          </OptionsSubSection>
        )}
        {interiorColors.length > 0 && (
          <OptionsSubSection
            ariaLabel="Interior color"
            id={INTERIOR_COLOR_SECTION_ID}
            isMobile={isMobile}
            title="Interior Color"
            withDivider={exteriorColors.length > 0}
          >
            {interiorColors.map(color => (
              <OptionSlideCard
                count={color.count}
                imageUrl={color.imageUrl}
                isGreyedOut={color.count === 0}
                isIncluded={isInteriorSingle}
                isSelected={color.code === selectedOptions?.interiorColorCode}
                key={color.code}
                name={color.name}
                onSelect={() => {
                  if (color.code === selectedOptions?.interiorColorCode) {
                    setPendingOptionDeselection('interior', color.code);
                  } else {
                    setPendingOptionSelection('interior', color.code);
                  }
                }}
                priceAdd={color.priceAdd}
                section="Interior Colors"
              />
            ))}
          </OptionsSubSection>
        )}
        {driveTypes.length > 0 && (
          <OptionsSubSection
            ariaLabel="Drivetrain"
            id={DRIVETRAIN_SECTION_ID}
            isMobile={isMobile}
            title="Drivetrain"
            withDivider={exteriorColors.length > 0 || interiorColors.length > 0}
          >
            {driveTypes.map(driveType => (
              <OptionSlideCard
                count={driveType.count}
                imageUrl={driveType.imageUrl}
                isGreyedOut={driveType.count === 0}
                isIncluded={isDriveTypeSingle}
                isSelected={driveType.name === selectedOptions?.driveType}
                key={driveType.name}
                name={driveType.name}
                onSelect={() => {
                  if (driveType.name === selectedOptions?.driveType) {
                    setPendingOptionDeselection('driveType', driveType.name);
                  } else {
                    setPendingOptionSelection('driveType', driveType.name);
                  }
                }}
                priceAdd={driveType.priceAdd}
                section="Drivetrain"
              />
            ))}
          </OptionsSubSection>
        )}
        {engines.length > 0 && (
          <OptionsSubSection
            ariaLabel="Engine"
            id={ENGINE_SECTION_ID}
            isMobile={isMobile}
            title="Engine"
            withDivider={
              driveTypes.length > 0 ||
              exteriorColors.length > 0 ||
              interiorColors.length > 0
            }
          >
            {engines.map(engine => (
              <OptionSlideCard
                count={engine.count}
                imageContain
                imageUrl={engine.imageUrl}
                isGreyedOut={engine.count === 0}
                isIncluded={isEngineSingle}
                isSelected={engine.code === selectedOptions?.engineCode}
                key={engine.code}
                name={engine.name}
                onSelect={() => {
                  if (engine.code === selectedOptions?.engineCode) {
                    setPendingOptionDeselection('engine', engine.code);
                  } else {
                    setPendingOptionSelection('engine', engine.code);
                  }
                }}
                priceAdd={engine.priceAdd}
                section="Engine"
              />
            ))}
          </OptionsSubSection>
        )}
      </SectionWrapper>
    </TrackWrapper>
  );
}

export default withErrorBoundary<OwnProps>(
  OptionsSection,
  undefined,
  undefined,
  SectionName.Options
);
